import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Activity,
  List,
  Download,
  FileText,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import AdminLogsTabs from './AdminLogsTabs';

const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  search: '',
  member_name: '',
  company: '',
  country: '',
  membership_type: '',
  status: '',
  last_login_date: '',
  last_inquiry_date: '',
  last_quote_date: '',
  date_from: '',
  date_to: '',
};

const INITIAL_SORT = { by: 'member_name', direction: 'asc' };

const displayValue = (value, fallback = '-') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const formatTimestamp = (value) => {
  if (!value) return 'Never';
  const text = String(value);
  const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? text : date.toLocaleString();
};

const getStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('active') || normalized.includes('approved') || normalized.includes('verified')) {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (normalized.includes('pending') || normalized.includes('hold')) {
    return 'bg-amber-100 text-amber-800';
  }
  if (normalized.includes('inactive') || normalized.includes('reject') || normalized.includes('suspend')) {
    return 'bg-rose-100 text-rose-800';
  }
  return 'bg-slate-100 text-slate-700';
};

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const buildParams = (filters, sort, page, includePagination = true) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  params.set('sort_by', sort.by);
  params.set('sort_direction', sort.direction);
  const timezoneName = getBrowserTimeZone();
  if (timezoneName) params.set('timezone_name', timezoneName);
  params.set('timezone_offset_minutes', String(new Date().getTimezoneOffset()));
  if (includePagination) {
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(page * PAGE_SIZE));
  }
  return params;
};

const SortButton = ({ label, field, sort, onSort }) => {
  const active = sort.by === field;
  const Icon = sort.direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 font-medium hover:text-slate-900"
      data-testid={`member-activity-sort-${field.replaceAll('_', '-')}`}
      aria-label={`Sort by ${label}`}
    >
      {label}
      {active && <Icon size={14} aria-hidden="true" />}
    </button>
  );
};

const AdminMemberActivity = () => {
  const { api, hasPermission } = useAdminAuth();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [sort, setSort] = useState(INITIAL_SORT);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [viewMode, setViewMode] = useState('stream');
  const [streamData, setStreamData] = useState({ events: [], total: 0 });
  const [streamPage, setStreamPage] = useState(0);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamFilters, setStreamFilters] = useState({ search: '', member_name: '', company: '', country: '', event_type: '', section: '', date_from: '', date_to: '' });
  const canExport = hasPermission('logs', 'export');

  const loadStream = useCallback(async ({ nextFilters = streamFilters, nextPage = streamPage } = {}) => {
    try {
      setStreamLoading(true);
      const params = new URLSearchParams();
      Object.entries(nextFilters).forEach(([key, value]) => value && params.set(key, value));
      params.set('limit', String(PAGE_SIZE));
      params.set('skip', String(nextPage * PAGE_SIZE));
      const response = await api.get(`/api/admin/member-activity/stream?${params.toString()}`);
      setStreamData({
        events: Array.isArray(response.data?.events) ? response.data.events : [],
        total: Number(response.data?.total || 0),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load member activity stream');
      setStreamData({ events: [], total: 0 });
    } finally {
      setStreamLoading(false);
    }
  }, [api, streamFilters, streamPage]);

  const load = useCallback(async ({ nextFilters, nextPage, nextSort }) => {
    try {
      setLoading(true);
      const params = buildParams(nextFilters, nextSort, nextPage);
      const response = await api.get(`/api/admin/member-activity?${params.toString()}`);
      setData({
        rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
        total: Number(response.data?.total || 0),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load member activity');
      setData({ rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load({ nextFilters: EMPTY_FILTERS, nextPage: 0, nextSort: INITIAL_SORT });
  }, [load]);

  useEffect(() => {
    if (viewMode === 'stream') loadStream({ nextFilters: streamFilters, nextPage: streamPage });
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyStreamFilters = (event) => {
    event.preventDefault();
    setStreamPage(0);
    loadStream({ nextFilters: streamFilters, nextPage: 0 });
  };

  const changeStreamPage = (nextPage) => {
    if (nextPage < 0 || nextPage * PAGE_SIZE >= streamData.total) return;
    setStreamPage(nextPage);
    loadStream({ nextFilters: streamFilters, nextPage });
  };

  const describeEvent = (event) => {
    if (event.event_type === 'login') return 'Logged in to the member portal';
    if (event.event_type === 'expression_of_interest') return 'Submitted an expression of interest';
    if (event.event_type === 'section_view') return `Opened ${displayValue(event.section, 'a portal section')}`;
    if (event.event_type === 'notification_received') return `Received ${displayValue(event.metadata?.resource, 'a platform notification')}`;
    const action = event.metadata?.action || 'Used';
    const resource = event.metadata?.resource || event.section || 'the portal';
    return `${action} ${resource}`;
  };

  const applyFilters = (event) => {
    event.preventDefault();
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setPage(0);
    load({ nextFilters, nextPage: 0, nextSort: sort });
  };

  const resetFilters = () => {
    const nextFilters = { ...EMPTY_FILTERS };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(0);
    load({ nextFilters, nextPage: 0, nextSort: sort });
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const updateSort = (field) => {
    const isSameField = sort.by === field;
    const nextSort = {
      by: field,
      direction: isSameField
        ? (sort.direction === 'asc' ? 'desc' : 'asc')
        : (field.startsWith('last_') ? 'desc' : 'asc'),
    };
    setSort(nextSort);
    setPage(0);
    load({ nextFilters: appliedFilters, nextPage: 0, nextSort });
  };

  const changePage = (nextPage) => {
    if (nextPage < 0 || nextPage * PAGE_SIZE >= data.total) return;
    setPage(nextPage);
    load({ nextFilters: appliedFilters, nextPage, nextSort: sort });
  };

  const exportRows = async (format) => {
    try {
      setExporting(format);
      const params = buildParams(appliedFilters, sort, page, true);
      params.set('format', format);
      const response = await api.get(`/api/admin/member-activity/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const mimeType = response.headers?.['content-type'] || (format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8');
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `member-activity-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Member activity exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export the current member activity result');
    } finally {
      setExporting('');
    }
  };

  const start = data.total ? (page * PAGE_SIZE) + 1 : 0;
  const end = Math.min((page + 1) * PAGE_SIZE, data.total);
  const hasPreviousPage = page > 0;
  const hasNextPage = (page + 1) * PAGE_SIZE < data.total;

  return (
    <div className="space-y-6" data-testid="admin-member-activity">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Admin Logs</h1>
        <p className="mt-1 text-slate-500">Review platform activity and member engagement from one place.</p>
      </div>

      <AdminLogsTabs active="member" />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
        <Button type="button" size="sm" variant={viewMode === 'stream' ? 'default' : 'ghost'} onClick={() => setViewMode('stream')}>
          <Activity size={16} className="mr-2" />Activity Stream
        </Button>
        <Button type="button" size="sm" variant={viewMode === 'summary' ? 'default' : 'ghost'} onClick={() => setViewMode('summary')}>
          <List size={16} className="mr-2" />Member Summary
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{viewMode === 'stream' ? 'Member Activity Stream' : 'Member Activity Summary'}</h2>
          <p className="mt-1 text-sm text-slate-500">{viewMode === 'stream' ? 'A chronological record of actions members take throughout the portal.' : 'Every member is listed, including members with no recorded activity.'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => viewMode === 'stream' ? loadStream({ nextFilters: streamFilters, nextPage: streamPage }) : load({ nextFilters: appliedFilters, nextPage: page, nextSort: sort })}
            disabled={viewMode === 'stream' ? streamLoading : loading}
            data-testid="member-activity-refresh"
          >
            <RefreshCw size={16} className={`mr-2 ${(viewMode === 'stream' ? streamLoading : loading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canExport && viewMode === 'summary' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => exportRows('csv')}
                disabled={Boolean(exporting)}
                data-testid="member-activity-export-csv"
              >
                <FileText size={16} className="mr-2" />
                {exporting === 'csv' ? 'Exporting…' : 'CSV'}
              </Button>
              <Button
                type="button"
                onClick={() => exportRows('xlsx')}
                disabled={Boolean(exporting)}
                data-testid="member-activity-export-xlsx"
              >
                <Download size={16} className="mr-2" />
                {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
              </Button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'summary' ? (<>
      <Card className="rounded-lg border-slate-200 shadow-none">
        <CardContent className="p-5">
          <form onSubmit={applyFilters} className="space-y-4" data-testid="member-activity-filters">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[250px] flex-1">
                <label htmlFor="member-activity-search" className="mb-1 block text-sm font-medium text-slate-700">Global search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="member-activity-search"
                    value={filters.search}
                    onChange={(event) => updateFilter('search', event.target.value)}
                    placeholder="Name, company, email, or country"
                    className="pl-9"
                    data-testid="member-activity-search"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="member-activity-apply-filters">
                  <SlidersHorizontal size={16} className="mr-2" />
                  Apply filters
                </Button>
                <Button type="button" variant="outline" onClick={resetFilters} data-testid="member-activity-reset-filters">
                  Reset
                </Button>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div>
                <label htmlFor="member-activity-member-name" className="mb-1 block text-xs font-medium text-slate-600">Member name</label>
                <Input id="member-activity-member-name" value={filters.member_name} onChange={(event) => updateFilter('member_name', event.target.value)} data-testid="member-activity-filter-member-name" />
              </div>
              <div>
                <label htmlFor="member-activity-company" className="mb-1 block text-xs font-medium text-slate-600">Company</label>
                <Input id="member-activity-company" value={filters.company} onChange={(event) => updateFilter('company', event.target.value)} data-testid="member-activity-filter-company" />
              </div>
              <div>
                <label htmlFor="member-activity-country" className="mb-1 block text-xs font-medium text-slate-600">Country</label>
                <Input id="member-activity-country" value={filters.country} onChange={(event) => updateFilter('country', event.target.value)} data-testid="member-activity-filter-country" />
              </div>
              <div>
                <label htmlFor="member-activity-membership-type" className="mb-1 block text-xs font-medium text-slate-600">Membership type</label>
                <Input id="member-activity-membership-type" value={filters.membership_type} onChange={(event) => updateFilter('membership_type', event.target.value)} data-testid="member-activity-filter-membership-type" />
              </div>
              <div>
                <label htmlFor="member-activity-status" className="mb-1 block text-xs font-medium text-slate-600">Status</label>
                <Input id="member-activity-status" value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} data-testid="member-activity-filter-status" />
              </div>
              <div>
                <label htmlFor="member-activity-last-login" className="mb-1 block text-xs font-medium text-slate-600">Last login date</label>
                <Input id="member-activity-last-login" type="date" value={filters.last_login_date} onChange={(event) => updateFilter('last_login_date', event.target.value)} data-testid="member-activity-filter-last-login" />
              </div>
              <div>
                <label htmlFor="member-activity-last-inquiry" className="mb-1 block text-xs font-medium text-slate-600">Last inquiry created</label>
                <Input id="member-activity-last-inquiry" type="date" value={filters.last_inquiry_date} onChange={(event) => updateFilter('last_inquiry_date', event.target.value)} data-testid="member-activity-filter-last-inquiry" />
              </div>
              <div>
                <label htmlFor="member-activity-last-quote" className="mb-1 block text-xs font-medium text-slate-600">Last quote submitted</label>
                <Input id="member-activity-last-quote" type="date" value={filters.last_quote_date} onChange={(event) => updateFilter('last_quote_date', event.target.value)} data-testid="member-activity-filter-last-quote" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="member-activity-date-from" className="mb-1 block text-xs font-medium text-slate-600">Any activity from</label>
                  <Input id="member-activity-date-from" type="date" value={filters.date_from} onChange={(event) => updateFilter('date_from', event.target.value)} data-testid="member-activity-filter-date-from" />
                </div>
                <div>
                  <label htmlFor="member-activity-date-to" className="mb-1 block text-xs font-medium text-slate-600">Any activity to</label>
                  <Input id="member-activity-date-to" type="date" value={filters.date_to} onChange={(event) => updateFilter('date_to', event.target.value)} data-testid="member-activity-filter-date-to" />
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      </>) : (<>
        <Card className="rounded-lg border-slate-200 shadow-none">
          <CardContent className="p-5">
            <form onSubmit={applyStreamFilters} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Input placeholder="Search member, company, email or country" value={streamFilters.search} onChange={(event) => setStreamFilters((current) => ({ ...current, search: event.target.value }))} />
              <Input placeholder="Member name" value={streamFilters.member_name} onChange={(event) => setStreamFilters((current) => ({ ...current, member_name: event.target.value }))} />
              <Input placeholder="Company" value={streamFilters.company} onChange={(event) => setStreamFilters((current) => ({ ...current, company: event.target.value }))} />
              <Input placeholder="Country" value={streamFilters.country} onChange={(event) => setStreamFilters((current) => ({ ...current, country: event.target.value }))} />
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={streamFilters.event_type} onChange={(event) => setStreamFilters((current) => ({ ...current, event_type: event.target.value }))}>
                <option value="">All activity types</option><option value="login">Login</option><option value="section_view">Section opened</option><option value="portal_action">Portal action</option><option value="notification_received">Update received</option><option value="expression_of_interest">Expression of interest</option>
              </select>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={streamFilters.section} onChange={(event) => setStreamFilters((current) => ({ ...current, section: event.target.value }))}>
                <option value="">All sections</option>{['Dashboard','Directory','Membership','Profile','Quote Tool','Event Connect','Messages','Notifications','Success Stories','Member Leaderboard','Team Management','Company Branches','Refer Partner','Analytics','M&A','Complaints','Rewards'].map((section) => <option key={section} value={section}>{section}</option>)}
              </select>
              <Input type="date" aria-label="Activity from" value={streamFilters.date_from} onChange={(event) => setStreamFilters((current) => ({ ...current, date_from: event.target.value }))} />
              <Input type="date" aria-label="Activity to" value={streamFilters.date_to} onChange={(event) => setStreamFilters((current) => ({ ...current, date_to: event.target.value }))} />
              <div className="flex gap-2 xl:col-span-4">
                <Button type="submit"><SlidersHorizontal size={16} className="mr-2" />Apply filters</Button>
                <Button type="button" variant="outline" onClick={() => { const reset = { search: '', member_name: '', company: '', country: '', event_type: '', section: '', date_from: '', date_to: '' }; setStreamFilters(reset); setStreamPage(0); loadStream({ nextFilters: reset, nextPage: 0 }); }}>Reset</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-none">
          <CardContent className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="font-semibold text-slate-900">All member events</h3>
              <p className="mt-0.5 text-sm text-slate-500">{streamData.total} recorded event{streamData.total === 1 ? '' : 's'}, newest first</p>
            </div>
            {streamLoading ? <div className="py-14 text-center text-sm text-slate-500">Loading activity stream…</div> : streamData.events.length === 0 ? <div className="py-14 text-center text-sm text-slate-500">No activity matches these filters.</div> : (
              <div className="divide-y divide-slate-100">
                {streamData.events.map((event) => (
                  <div key={event.event_id} className="flex gap-4 px-5 py-4 hover:bg-slate-50/70">
                    <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-700"><Activity size={17} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-900">{displayValue(event.member_name)}</span>
                        <Badge className="bg-slate-100 text-slate-700">{displayValue(event.section, 'Portal')}</Badge>
                        <span className="text-sm text-slate-700">{describeEvent(event)}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{displayValue(event.company)} · {displayValue(event.email, 'No email')} · {displayValue(event.country)} · {formatTimestamp(event.occurred_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button type="button" variant="outline" size="sm" onClick={() => changeStreamPage(streamPage - 1)} disabled={streamPage === 0 || streamLoading}>Previous</Button>
              <span className="min-w-20 text-center text-sm text-slate-600">Page {streamPage + 1}</span>
              <Button type="button" variant="outline" size="sm" onClick={() => changeStreamPage(streamPage + 1)} disabled={(streamPage + 1) * PAGE_SIZE >= streamData.total || streamLoading}>Next</Button>
            </div>
          </CardContent>
        </Card>
      </>)}

      {viewMode === 'summary' && (<Card className="rounded-lg border-slate-200 shadow-none">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="font-semibold text-slate-900">Member activity tracker</h3>
              <p className="mt-0.5 text-sm text-slate-500">{data.total} matching member{data.total === 1 ? '' : 's'}</p>
            </div>
            <p className="text-sm text-slate-500" data-testid="member-activity-page-summary">
              {data.total ? `Showing ${start}–${end} of ${data.total}` : 'No matching members'}
            </p>
          </div>

          {loading ? (
            <div className="py-14 text-center text-sm text-slate-500">Loading member activity…</div>
          ) : data.rows.length === 0 ? (
            <div className="py-14 text-center text-sm text-slate-500">No members match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1750px] text-left text-sm" data-testid="member-activity-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-5 py-3"><SortButton label="Member Name" field="member_name" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3"><SortButton label="Company" field="company" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3"><SortButton label="Country" field="country" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3">Membership Type</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3"><SortButton label="Last Login" field="last_login" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3"><SortButton label="Last Inquiry Created" field="last_inquiry_created" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3"><SortButton label="Last Quote Submitted" field="last_quote_submitted" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3"><SortButton label="Last Platform Activity" field="last_platform_activity" sort={sort} onSort={updateSort} /></th>
                    <th className="px-5 py-3">Last Section</th>
                    <th className="px-5 py-3"><SortButton label="Last Expression of Interest" field="last_expression_of_interest" sort={sort} onSort={updateSort} /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map((row) => (
                    <tr key={row.member_id || row.email} className="align-top hover:bg-slate-50/70">
                      <td className="px-5 py-4">
                        <p className="font-medium text-slate-900">{displayValue(row.member_name)}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{displayValue(row.email, 'No email')}</p>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{displayValue(row.company || row.company_name)}</td>
                      <td className="px-5 py-4 text-slate-700">{displayValue(row.country)}</td>
                      <td className="px-5 py-4 text-slate-700">{displayValue(row.membership_type || row.tier)}</td>
                      <td className="px-5 py-4"><Badge className={getStatusClass(row.status)}>{displayValue(row.status)}</Badge></td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-600">{formatTimestamp(row.last_login)}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-600">{formatTimestamp(row.last_inquiry_created)}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-600">{formatTimestamp(row.last_quote_submitted)}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-600">{formatTimestamp(row.last_platform_activity)}</td>
                      <td className="px-5 py-4 text-slate-700">{displayValue(row.last_section)}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-600">{formatTimestamp(row.last_expression_of_interest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
            <Button type="button" variant="outline" size="sm" onClick={() => changePage(page - 1)} disabled={!hasPreviousPage || loading} data-testid="member-activity-previous-page">Previous</Button>
            <span className="min-w-20 text-center text-sm text-slate-600">Page {page + 1}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => changePage(page + 1)} disabled={!hasNextPage || loading} data-testid="member-activity-next-page">Next</Button>
          </div>
        </CardContent>
      </Card>)}
    </div>
  );
};

export default AdminMemberActivity;
